import * as React from "react";
import { cn } from "@/lib/utils";

import "../data-foundations.css";

type DataScrollAreaProps = React.HTMLAttributes<HTMLDivElement> & {
  orientation?: "horizontal" | "vertical" | "both";
  stableGutter?: boolean;
};

export const DataScrollArea = React.forwardRef<
  HTMLDivElement,
  DataScrollAreaProps
>(
  (
    { className, orientation = "horizontal", stableGutter = true, ...props },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        "data-scroll-area",
        `data-scroll-area--${orientation}`,
        className,
      )}
      data-scroll-owner={stableGutter ? "stable" : "auto"}
      {...props}
    />
  ),
);
DataScrollArea.displayName = "DataScrollArea";

type DataTableProps = React.TableHTMLAttributes<HTMLTableElement> & {
  caption?: React.ReactNode;
  captionClassName?: string;
  scrollAreaClassName?: string;
  scrollAreaLabel?: string;
};

export const DataTable = React.forwardRef<HTMLTableElement, DataTableProps>(
  (
    {
      caption,
      captionClassName,
      children,
      className,
      scrollAreaClassName,
      scrollAreaLabel,
      ...props
    },
    ref,
  ) => (
    <DataScrollArea
      className={scrollAreaClassName}
      aria-label={scrollAreaLabel}
      role={scrollAreaLabel ? "region" : undefined}
      tabIndex={scrollAreaLabel ? 0 : undefined}
    >
      <table ref={ref} className={cn("data-table", className)} {...props}>
        {caption ? (
          <caption className={captionClassName}>{caption}</caption>
        ) : null}
        {children}
      </table>
    </DataScrollArea>
  ),
);
DataTable.displayName = "DataTable";

export const DataList = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn("data-list", className)} {...props} />
));
DataList.displayName = "DataList";

export const DataSurface = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("data-surface", className)} {...props} />
));
DataSurface.displayName = "DataSurface";
